import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Activity, Bot, Eye, FileClock, Gauge, ListChecks, RefreshCw, RotateCcw, Search, ShieldCheck } from 'lucide-react'
import { useState, type FormEvent, type KeyboardEvent, type ReactNode } from 'react'
import { Badge, Button, Card, EmptyState, Field, Input, Modal, PageTitle, Textarea } from '../components/ui'
import { api } from '../lib/api'
import type { AdminScoreRunSummary } from '../lib/types'

type ScoreRunsResponse = { items: AdminScoreRunSummary[]; total: number }
type JsonRecord = Record<string, unknown>

const kindLabel = (kind: string) => kind === 'static' ? '静态评分' : kind === 'dynamic' ? '动态评分' : kind === 'final' ? '最终分' : kind === 'manual_adjustment' ? '人工调分' : kind
const statusLabel = (status: string) => status === 'queued' ? '排队中' : status === 'running' ? '执行中' : status === 'completed' ? '已完成' : status === 'failed' ? '失败' : status === 'skipped' ? '已跳过' : status || '未知'
const dimensionLabel = (key: string) => ({
  functionality: '功能完整性',
  engineering_quality: '工程质量',
  technical_depth: '技术深度',
  code_quality: '代码质量',
  usability: '易用性',
  reliability: '稳定性',
  performance: '性能表现',
  innovation: '创新性',
}[key] || key.replace(/_/g, ' '))

export function AdminScorePage() {
  const qc = useQueryClient()
  const [searchInput, setSearchInput] = useState('')
  const [searchText, setSearchText] = useState('')
  const [kind, setKind] = useState('')
  const [status, setStatus] = useState('')
  const [activeOnly, setActiveOnly] = useState(true)
  const [detail, setDetail] = useState<AdminScoreRunSummary | null>(null)
  const [retry, setRetry] = useState<AdminScoreRunSummary | null>(null)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const runs = useQuery({
    queryKey: ['admin-score-runs', searchText, kind, status, activeOnly],
    queryFn: () => api<ScoreRunsResponse>(`/api/v1/admin/score-runs?limit=200${searchText ? `&q=${encodeURIComponent(searchText)}` : ''}${kind ? `&score_kind=${kind}` : ''}${status ? `&status=${status}` : ''}${activeOnly ? '&active_only=true' : ''}`),
  })
  const items = runs.data?.items || []
  const manualCount = items.filter(item => item.score_kind === 'manual_adjustment').length
  const failedCount = items.filter(item => item.status === 'failed').length

  function submitSearch(event: FormEvent) {
    event.preventDefault()
    setSearchText(searchInput.trim())
  }

  return <div className="workspace-page data-center-page">
    <PageTitle eyebrow="ADMIN · SCORING" title="评分管理" description="审计比赛资产的静态评分、动态评分、最终分与人工调分流水；所有变更均追加记录并保留历史。" actions={<Button variant="secondary" onClick={() => runs.refetch()}><RefreshCw size={15}/>刷新</Button>}/>
    {message && <div className="form-success page-message">{message}</div>}{error && <div className="form-error page-message">{error}</div>}
    <div className="metrics-row"><Metric icon={<Activity/>} label="当前流水" value={runs.data?.total || 0}/><Metric icon={<ShieldCheck/>} label="人工调分" value={manualCount}/><Metric icon={<FileClock/>} label="失败记录" value={failedCount}/></div>
    <Card className="data-center-filters">
      <form className="data-center-search" onSubmit={submitSearch}>
        <div className="search-box"><Search size={16}/><Input value={searchInput} onChange={event => setSearchInput(event.target.value)} placeholder="按作品名称、作者、评分记录或 Asset ID 搜索"/></div>
        <Button type="submit" variant="secondary">搜索</Button>
        {searchText && <Button type="button" variant="ghost" onClick={() => { setSearchInput(''); setSearchText('') }}>清除</Button>}
      </form>
      <div className="score-run-filters">
        {['', 'static', 'dynamic', 'final', 'manual_adjustment'].map(value => <button type="button" key={value || 'all'} className={kind === value ? 'active' : ''} onClick={() => setKind(value)}>{value ? kindLabel(value) : '全部类型'}</button>)}
        {['', 'queued', 'running', 'completed', 'failed', 'skipped'].map(value => <button type="button" key={value || 'status-all'} className={status === value ? 'active' : ''} onClick={() => setStatus(value)}>{value ? statusLabel(value) : '全部状态'}</button>)}
        <label><input type="checkbox" checked={activeOnly} onChange={event => setActiveOnly(event.target.checked)}/>仅当前有效</label>
      </div>
    </Card>
    {runs.isLoading ? <div className="page-loader">正在读取评分流水…</div> : !items.length ? <EmptyState title="没有匹配的评分流水" description="切换筛选条件，或先在资产管理中触发评分。"/> : <div className="score-run-list">{items.map(item => <ScoreRunItem key={item.id} item={item} open={() => setDetail(item)} retry={() => setRetry(item)}/>)}</div>}
    {detail && <ScoreRunDetail item={detail} close={() => setDetail(null)}/>}
    {retry && <RetryScoreModal item={retry} close={() => setRetry(null)} done={async text => { setRetry(null); setMessage(text); setError(''); await qc.invalidateQueries({ queryKey: ['admin-score-runs'] }) }} fail={text => { setError(text); setMessage('') }}/>}
  </div>
}

function ScoreRunItem({ item, open, retry }: { item: AdminScoreRunSummary; open: () => void; retry: () => void }) {
  const title = item.submission_agent_name || item.asset_name || shortId(item.asset_id)
  const author = item.author_display_name || item.author_username || '未知作者'
  function openFromKeyboard(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      open()
    }
  }
  return <Card className="score-run-card score-run-clickable" role="button" tabIndex={0} onClick={open} onKeyDown={openFromKeyboard}>
    <div className="score-run-main">
      <div className="score-run-icon"><Bot size={18}/></div>
      <div className="score-run-title">
        <strong>{title}</strong>
        <small>{author} · {shortId(item.asset_id)} · {shortId(item.id)}</small>
      </div>
    </div>
    <div className="score-run-tags">
      <Badge tone={item.score_kind === 'manual_adjustment' ? 'amber' : item.score_kind === 'final' ? 'green' : 'blue'}>{kindLabel(item.score_kind)}</Badge>
      <Badge tone={statusTone(item.status)}>{statusLabel(item.status)}{item.is_active ? '' : ' · 历史'}</Badge>
      {item.triggered_by_admin && <Badge tone="amber">管理员</Badge>}
    </div>
    <div className="score-run-score"><strong>{formatScore(item.effective_score)}</strong><small>{item.score_kind === 'final' ? '/105' : '/100'}</small></div>
    <div className="score-run-policy">
      <span>{item.scoring_policy_id || '无评分策略'}</span>
      <small>{item.scoring_policy_version || '默认版本'}</small>
    </div>
    <time>{new Date(item.created_at).toLocaleString('zh-CN', { hour12: false })}</time>
    <div className="score-run-actions">
      <Button type="button" variant="secondary" onClick={event => { event.stopPropagation(); open() }}><Eye size={15}/>详情</Button>
      {(item.score_kind === 'static' || item.score_kind === 'dynamic') && <Button type="button" variant="secondary" onClick={event => { event.stopPropagation(); retry() }}><RotateCcw size={15}/>重新触发</Button>}
    </div>
  </Card>
}

function ScoreRunDetail({ item, close }: { item: AdminScoreRunSummary; close: () => void }) {
  const result = asRecord(item.result_json)
  const snapshot = asRecord(item.scoring_policy_snapshot_json)
  const dimensions = dimensionItems(result)
  const rounds = roundItems(result)
  const reasons = reasonTexts(item, result)
  const composition = compositionItems(item, result, snapshot)
  return <Modal open onOpenChange={value => !value && close()} title="评分详情" description={`${item.submission_agent_name || item.asset_name || shortId(item.asset_id)} · ${kindLabel(item.score_kind)}`} className="score-detail-dialog">
    <div className="score-detail">
      <header className="score-detail-hero">
        <div><small>有效得分</small><strong>{formatScore(item.effective_score)}</strong></div>
        <div><small>状态</small><Badge tone={statusTone(item.status)}>{statusLabel(item.status)}{item.is_active ? '' : ' · 历史'}</Badge></div>
        <div><small>时间</small><span>{new Date(item.created_at).toLocaleString('zh-CN', { hour12: false })}</span></div>
      </header>
      <DetailSection icon={<Gauge/>} title="评分组成">
        <div className="score-detail-grid">
          {composition.map(row => <DetailFact key={row.label} label={row.label} value={row.value}/>)}
        </div>
        {dimensions.length > 0 && <div className="score-dimensions">{dimensions.map(dim => <div className="score-dimension" key={dim.key}>
          <div><strong>{dim.label}</strong><span>{formatScore(dim.score)}</span></div>
          {dim.weight && <small>权重 {dim.weight}</small>}
          {dim.reason && <p>{dim.reason}</p>}
          {dim.children.length > 0 && <div className="score-subdimensions">{dim.children.map(child => <span key={child.key}>{child.label} {formatScore(child.score)}</span>)}</div>}
        </div>)}</div>}
      </DetailSection>
      <DetailSection icon={<ShieldCheck/>} title="打分理由">
        {reasons.length ? <div className="score-reasons">{reasons.map((reason, index) => <p key={`${index}-${reason}`}>{reason}</p>)}</div> : <small className="score-empty">当前记录没有结构化理由。</small>}
      </DetailSection>
      <DetailSection icon={<ListChecks/>} title="评分过程">
        {rounds.length ? <div className="score-rounds">{rounds.map((round, index) => <div className="score-round" key={index}>
          <strong>{round.title}</strong>
          <span>{round.score == null ? '—' : formatScore(round.score)}</span>
          {round.reason && <p>{round.reason}</p>}
        </div>)}</div> : <ProcessSummary item={item} result={result}/>}
      </DetailSection>
      <details className="score-raw-json">
        <summary>原始评分证据</summary>
        <pre>{JSON.stringify({ result_json: item.result_json, scoring_policy_snapshot_json: item.scoring_policy_snapshot_json, error_message: item.error_message }, null, 2)}</pre>
      </details>
    </div>
  </Modal>
}

function DetailSection({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return <section className="score-detail-section"><header><span>{icon}</span><h3>{title}</h3></header>{children}</section>
}

function DetailFact({ label, value }: { label: string; value: ReactNode }) {
  return <div className="score-detail-fact"><small>{label}</small><span>{value}</span></div>
}

function ProcessSummary({ item, result }: { item: AdminScoreRunSummary; result: JsonRecord | null }) {
  const started = valueText(result?.started_at)
  const completed = valueText(result?.completed_at || item.completed_at)
  return <div className="score-process-summary">
    <DetailFact label="评分来源" value={item.triggered_by_admin ? '管理员操作' : item.source_job_id ? '自动评分任务' : '系统回填'}/>
    <DetailFact label="任务 ID" value={item.source_job_id || item.evaluation_run_id || '—'}/>
    <DetailFact label="开始时间" value={started || '未记录'}/>
    <DetailFact label="完成时间" value={completed || '未记录'}/>
    {item.error_message && <p>{item.error_message}</p>}
  </div>
}

function RetryScoreModal({ item, close, done, fail }: { item: AdminScoreRunSummary; close: () => void; done: (text: string) => void | Promise<void>; fail: (text: string) => void }) {
  const [reason, setReason] = useState(`管理员在评分管理中重新触发${kindLabel(item.score_kind)}`)
  const [busy, setBusy] = useState(false)
  async function submit(event: FormEvent) {
    event.preventDefault()
    if (item.score_kind !== 'static' && item.score_kind !== 'dynamic') return
    setBusy(true)
    try {
      const response = await api<{ message: string }>(`/api/v1/admin/assets/${item.asset_id}/score/${item.score_kind}`, { method: 'POST', body: { reason, confirm: true } })
      await done(response.message)
    } catch (error) {
      fail(error instanceof Error ? error.message : '重新触发失败')
    } finally {
      setBusy(false)
    }
  }
  return <Modal open onOpenChange={value => !value && close()} title="重新触发评分" description={`${item.submission_agent_name || item.asset_name || shortId(item.asset_id)} · ${kindLabel(item.score_kind)}`}>
    <form className="stack" onSubmit={submit}>
      <Field label="触发原因" required><Textarea value={reason} onChange={event => setReason(event.target.value)} required minLength={2}/></Field>
      <div className="modal-actions-inline"><Button type="submit" loading={busy}><RotateCcw size={15}/>重新触发</Button><Button type="button" variant="secondary" onClick={close}>取消</Button></div>
    </form>
  </Modal>
}

function compositionItems(item: AdminScoreRunSummary, result: JsonRecord | null, snapshot: JsonRecord | null) {
  const rows = [
    { label: '原始分', value: formatScore(numberValue(result?.score_raw) ?? item.score_100) },
    { label: '有效分', value: formatScore(item.effective_score) },
  ]
  if (item.score_kind === 'manual_adjustment') {
    rows.push({ label: '调整前', value: formatScore(numberValue(result?.base_score)) })
    rows.push({ label: '调整值', value: formatSigned(numberValue(result?.adjustment) ?? item.score_100) })
  }
  if (item.score_kind === 'final') {
    rows.push({ label: '静态分', value: formatScore(numberValue(result?.static_score)) })
    rows.push({ label: '动态分', value: formatScore(numberValue(result?.dynamic_score)) })
  }
  rows.push({ label: '静态权重', value: percentValue(snapshot?.static_weight) })
  rows.push({ label: '动态权重', value: percentValue(snapshot?.dynamic_weight) })
  return rows
}

function dimensionItems(result: JsonRecord | null) {
  const dimensions = asRecord(result?.dimensions)
  const weights = asRecord(result?.weights)
  if (!dimensions) return []
  return Object.entries(dimensions).map(([key, raw]) => {
    const row = asRecord(raw)
    const children = asRecord(row?.subdimensions)
    return {
      key,
      label: labelOf(row?.name, dimensionLabel(key)),
      score: numberValue(row?.score) ?? numberValue(row?.score_100),
      weight: percentValue(numberValue(weights?.[key]) ?? numberValue(row?.weight), true),
      reason: valueText(row?.rationale || row?.reason || row?.summary),
      children: children ? Object.entries(children).map(([childKey, childRaw]) => {
        const child = asRecord(childRaw)
        return { key: childKey, label: labelOf(child?.name, dimensionLabel(childKey)), score: numberValue(child?.score) ?? numberValue(child?.score_100) }
      }) : [],
    }
  })
}

function roundItems(result: JsonRecord | null) {
  const rounds = Array.isArray(result?.rounds) ? result.rounds : Array.isArray(result?.cases) ? result.cases : []
  return rounds.map((raw, index) => {
    const row = asRecord(raw)
    return {
      title: valueText(row?.name || row?.input || row?.case_id) || `第 ${index + 1} 轮`,
      score: numberValue(row?.score) ?? numberValue(row?.score_100),
      reason: valueText(row?.reason || row?.rationale || row?.error),
    }
  })
}

function reasonTexts(item: AdminScoreRunSummary, result: JsonRecord | null) {
  const values = [
    item.adjustment_reason,
    valueText(result?.summary),
    valueText(result?.reason),
    valueText(result?.rationale),
    valueText(result?.conclusion),
    item.error_message,
  ].filter(Boolean) as string[]
  return [...new Set(values)]
}

function statusTone(status: string): 'blue' | 'green' | 'amber' | 'red' | 'neutral' {
  if (status === 'completed' || status === 'skipped') return 'green'
  if (status === 'failed') return 'red'
  if (status === 'running' || status === 'queued') return 'amber'
  return 'neutral'
}

function Metric({ icon, label, value }: { icon: ReactNode; label: string; value: number }) { return <Card className="metric-card"><span>{icon}</span><div><strong>{value}</strong><small>{label}</small></div></Card> }

function asRecord(value: unknown): JsonRecord | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as JsonRecord : null
}

function numberValue(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function valueText(value: unknown): string {
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return ''
}

function labelOf(value: unknown, fallback: string) {
  const text = valueText(value)
  return text ? dimensionLabel(text) : fallback
}

function formatScore(value: unknown) {
  const score = numberValue(value)
  return score == null ? '—' : score.toFixed(1)
}

function formatSigned(value: unknown) {
  const score = numberValue(value)
  if (score == null) return '—'
  return `${score > 0 ? '+' : ''}${score.toFixed(1)}`
}

function percentValue(value: unknown, blankWhenMissing = false) {
  const num = numberValue(value)
  if (num == null) return blankWhenMissing ? '' : '—'
  return `${Math.round(num * 100)}%`
}

function shortId(id: string) {
  return id.length > 14 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id
}
